import { useCallback, useRef, useState } from "react";
import { Card, Dropdown, Form, Image } from "react-bootstrap";
import { TfiInfoAlt } from "react-icons/tfi";
import { CgProfile } from "react-icons/cg";
import { BsChatLeft } from "react-icons/bs";
import { useEffect } from "react";
import Moment from "react-moment";
import dayjs from "dayjs";
import { inboxServices } from "../../APIServices/SitePilotManagement/inboxServices";
import InboxChats from "./InboxChats";
import { FiBell, FiChevronDown } from "react-icons/fi";
import { formatDuration, formatTime } from "../../APIHelpers/utils";
import { useLiveDuration } from "../../Hooks/DurationHook";
import { CiViewTimeline } from "react-icons/ci";
import { FaWhatsapp } from "react-icons/fa";
import ContactSMS from "../Contacts/Componants/ContactSMS";
import ContactTickets from "../Contacts/Componants/ContactTickets";
import ContactNotes from "../Contacts/Componants/ContactNotes";

const InfoRow = ({ label, value }) => (
  <div className="hstack justify-content-between py-1">
    <span className="text-muted">{label}</span>
    <span className="fw-medium text-end">{value || "-"}</span>
  </div>
);

const SocialRow = ({ label, value }) => (
  <div className="hstack justify-content-between py-1">
    <span className="text-muted">{label}</span>
    {value && value !== "-" ? (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="fw-medium text-end"
      >
        {value}
      </a>
    ) : (
      <span className="fw-medium">-</span>
    )}
  </div>
);

const getLocation = (deviceInfo) => {
  const parts = [
    deviceInfo?.city || "",
    deviceInfo?.regionName || "",
    deviceInfo?.country || "",
    deviceInfo?.zip || "",
  ]
    .map(v => v?.toString().trim())
    .filter(v => v);
  return parts.length ? parts.join(", ") : "_";
};

const InboxDetails = ({
  inboxId,
  sessionId,
  setSessionId,
  currentUserData,
  setInboxData,
  inboxData,
  deleteSession,
  category,
  setInboxs
}) => {
  const [view, setView] = useState("profile");
  const [openHistory, setOpenHistory] = useState("chat");
  const [pageViews, setPageViews] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [messages, setMessages] = useState([]);
  const [totalMessage, setTotalMessage] = useState(0);
  const [lastRepliedBy, setLastRepliedBy] = useState("");
  const [lastRepliedDT, setLastRepliedDT] = useState("");

  const MoreActionOption = [
    {
      label: "Twilio SMS",
      icon: <CiViewTimeline />,
    },
    {
      label: "Ticket",
      icon: <BsChatLeft />,
    },
    {
      label: "WhatsApp",
      icon: <FaWhatsapp />,
    }
  ];

  const updateInboxList = useCallback(async (inbox) => {
    const onlyMessages = messages.filter((m) => m.type === "message");
    const notifications = messages.filter((m) => m.type !== "message");
    const lastMessage = onlyMessages[onlyMessages.length - 1];
    setInboxs((prev) =>
      prev.map((item) => {
        if (
          item.inboxId === inbox.inboxId &&
          item.sessionId === inbox.sessionId
        ) {
          return {
            ...item,
            totalNotification: notifications.length,
            totalMessage: onlyMessages.length,
            lastMessage: lastMessage?.message || "",
            lastRepliedBy: lastMessage?.userType || "",
            lastReplaiedDT: lastMessage?.createdAt || "",
            chatStatus: inbox.chatStatus ?? item.chatStatus,
            sessionStatus: inbox.sessionStatus ?? item.sessionStatus,
            updatedAt: inbox.updatedAt ?? item.updatedAt,
          };
        }
        return item;
      })
    );
  }, []);

  const updateInboxListRef = useRef(updateInboxList);

  useEffect(() => {
    updateInboxListRef.current = updateInboxList;
  }, [updateInboxList]);

  const getSingleInbox = async () => {
    let data = await inboxServices.getSingleInbox(inboxId, sessionId);
    const inbox = data?.data || {};
    const msgs = inbox?.messages || [];
    setInboxData(inbox);
    setMessages(msgs);
  };

  useEffect(() => {
    if (inboxId && sessionId) {
      getSingleInbox();
    }
  }, [inboxId, sessionId]);

  useEffect(() => {
    updateInboxListRef.current(inboxData);
    if (!messages?.length) {
      setNotifications([]);
      setTotalMessage(0);
      setPageViews(0);
      setLastRepliedBy("");
      setLastRepliedDT("");
      return;
    }

    const notifications = messages.filter(
      (item) => item.type !== "message"
    );
    setNotifications(notifications);

    const onlyMessages = messages.filter(
      (item) => item.type === "message"
    );
    setTotalMessage(onlyMessages.length);

    const pageViews = messages.filter(
      (item) => item.type === "navigate"
    ).length;
    setPageViews(pageViews);

    const lastMessage = onlyMessages[onlyMessages.length - 1];
    setLastRepliedBy(lastMessage?.userType || "");
    setLastRepliedDT(lastMessage?.createdAt || "");

  }, [messages, inboxData]);

  const getChatDateLabel = (date) => {
    const msgDate = dayjs(date);
    const today = dayjs();
    if (msgDate.isSame(today, "day")) {
      return "Today";
    }
    if (msgDate.isSame(today.subtract(1, "day"), "day")) {
      return "Yesterday";
    }
    if (msgDate.isSame(today, "week")) {
      return msgDate.format("dddd");
    }
    return msgDate.format("dddd, DD MMM YYYY");
  };

  const liveDuration = useLiveDuration(inboxData.startedDT, !(inboxData.chatStatus === "closed" || inboxData.sessionStatus === "closed"));

  return (
    <>
      <div className="d-flex">
        <div className={`overflow-auto border-end`} style={{ height: "calc(100vh - 108px)", width: "40%" }}>
          <Card className="border-0">
            <Card.Body className="p-0 d-flex flex-column">
              <div className="preview-header">
                <Image src={inboxData?.icon} roundedCircle width={50} height={50} />
                {inboxData?.type === "visitor" ?
                  <h6 className="mt-2 mb-0">{inboxData?.inboxId}</h6>
                  :
                  <>
                    <h6 className="mt-2 mb-0">{inboxData?.name}</h6>
                    <small className="text-muted">{inboxData?.email}</small>
                  </>
                }
              </div>
              <div className="preview-switcher">
                <button
                  className={`hstack gap-2 justify-content-center ${view === "profile" && "active"}`}
                  onClick={() => setView("profile")}
                >
                  <CgProfile /> Profile
                </button>
                <button
                  className={`hstack gap-2 justify-content-center ${view === "notification" && "active"}`}
                  onClick={() => setView("notification")}
                >
                  <FiBell /> Notification
                </button>
                <button
                  className={`hstack gap-2 justify-content-center ${view === "more" && "active"}`}
                  onClick={() => setView("more")}
                >
                  <TfiInfoAlt />
                  More Info
                </button>
              </div>

              <div className="preview-scroll overflow-auto" style={{ height: `calc(100vh - ${inboxData?.type === "inbox" ? "288" : "245"}px)` }}>
                {view === "profile" && (
                  <div className="preview-block">
                    <Form.Label>Activity Info</Form.Label>
                    <InfoRow label="Total Message" value={totalMessage || 0} />
                    <InfoRow label="Total Notification" value={notifications?.length || 0} />
                    <InfoRow label="Total Page View" value={pageViews} />
                    <InfoRow label="Assign Agent" value={inboxData?.agentName || ""} />
                    <InfoRow label="Last Replied By" value={lastRepliedBy === "agent" ? "Agent" : lastRepliedBy === "visitor" ? "Visitor" : ""} />
                    <InfoRow
                      label="Last Replied Date | Time"
                      value={
                        lastRepliedDT ?
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {lastRepliedDT}
                          </Moment>
                          : ""
                      }
                    />
                    <InfoRow label="Chat Duration" value={inboxData?.chatStatus === "closed" ? formatDuration(inboxData.startedDT, inboxData.endedDT) : liveDuration} />
                    {inboxData?.type === "inbox" &&
                      <>
                        <hr />
                        <Form.Label>Customer Info</Form.Label>
                        <InfoRow label="Name" value={inboxData?.name} />
                        <InfoRow label="Email" value={inboxData?.email} />
                        <InfoRow label="Phone" value={inboxData?.phone} />
                        <InfoRow label="Company Name" value={inboxData?.companyName} />
                        {(inboxData?.additionalInfo || [])?.map((item, index) => (
                          <InfoRow key={index} label={item?.label} value={item.value} />
                        ))}
                        {(inboxData?.social || [])?.map((item, index) => (
                          <SocialRow key={index} label={item?.title} value={item.url} />
                        ))}
                      </>
                    }
                  </div>
                )}
                {view === "notification" && (
                  <div className="preview-block">
                    {notifications?.map((item, i) => {
                      const currentLabel = getChatDateLabel(item.createdAt);
                      const prevLabel =
                        i > 0 ? getChatDateLabel(notifications[i - 1].createdAt) : null;
                      const showDateDivider = i === 0 || currentLabel !== prevLabel;
                      return (
                        <>
                          {showDateDivider && (
                            <div
                              className={`text-center ${i === 0 ? "mb-3" : (i + 1) === notifications?.length ? "mt-3" : "my-3"}`}>
                              <span
                                className="px-3 py-1 rounded bg-light text-muted"
                                style={{ fontSize: 12 }}
                              >
                                {currentLabel}
                              </span>
                            </div>
                          )}
                          <div className="d-flex gap-2 w-100 mb-3">
                            <FiBell className="mt-2" />
                            <div className="p-2 gap-2 bg-light rounded w-100 d-flex justify-content-between align-items-end" style={{ fontSize: 12, borderLeft: "3px solid #0d6efd" }}>
                              {item.type === "navigate" ?
                                <span>Visitor has Navigated to the <a href={item.pageURL} target="__blank">{item.pageName}</a> page.</span>
                                :
                                <span>{item.message}</span>
                              }
                              <span className="text-nowrap"
                                style={{
                                  fontSize: 11,
                                  color: "#6c757d",
                                  marginTop: 2,
                                }}
                              >
                                {formatTime(item.createdAt)}
                              </span>
                            </div>
                          </div>
                        </>
                      )
                    })}
                  </div>
                )}
                {view === "more" && (
                  <div className="preview-block">
                    <Form.Label>Additional Info</Form.Label>
                    <InfoRow label="Created Date | Time"
                      value={
                        <Moment format="DD MMM YYYY | HH:mm" withTitle>
                          {inboxData?.createdAt}
                        </Moment>
                      }
                    />
                    <InfoRow label="Updated Date | Time"
                      value={
                        <Moment format="DD MMM YYYY | HH:mm" withTitle>
                          {inboxData?.updatedAt}
                        </Moment>
                      }
                    />
                    <InfoRow label="First Seen Date | Time"
                      value={
                        inboxData?.startedDT ?
                          <Moment format="DD MMM YYYY | HH:mm" withTitle>
                            {inboxData?.startedDT}
                          </Moment>
                          : ""
                      }
                    />
                    <InfoRow label="Last Seen Date | Time"
                      value={
                        <Moment format="DD MMM YYYY | HH:mm" withTitle>
                          {inboxData?.endedDT || inboxData?.lastActivityDT}
                        </Moment>
                      }
                    />
                    <hr />
                    <Form.Label>Location Info</Form.Label>
                    <InfoRow label="Ip Address" value={inboxData?.deviceInfo?.ipAddress} />
                    <InfoRow label="Country" value={getLocation(inboxData?.deviceInfo?.country)} />
                    <InfoRow label="City" value={getLocation(inboxData?.deviceInfo?.city)} />
                    <InfoRow label="Region" value={getLocation(inboxData?.deviceInfo?.regionName)} />
                    <InfoRow label="Zip Code" value={getLocation(inboxData?.deviceInfo?.zip)} />
                    <InfoRow label="Timezone" value={inboxData?.deviceInfo?.timezone} />
                    <hr />
                    <Form.Label>Device Info</Form.Label>
                    <InfoRow label="Browser Name" value={inboxData?.deviceInfo?.browser?.name} />
                    <InfoRow label="Browser Version" value={inboxData?.deviceInfo?.browser?.version} />
                    <InfoRow label="OS Name" value={inboxData?.deviceInfo?.os?.name} />
                    <InfoRow label="OS Version" value={inboxData?.deviceInfo?.os?.version} />
                    <InfoRow label="Device" value={inboxData?.deviceInfo?.device?.type} />
                    <hr />
                    <Form.Label>Web Session</Form.Label>
                    <InfoRow label="First Session"
                      value={
                        <Moment format="DD MMM YYYY | HH:mm" withTitle>
                          {inboxData?.createdAt}
                        </Moment>
                      }
                    />
                    <InfoRow label="Latest Session"
                      value={
                        <Moment format="DD MMM YYYY | HH:mm" withTitle>
                          {inboxData?.endedDT || inboxData?.lastActivityDT}
                        </Moment>
                      }
                    />
                    <InfoRow label="Total Session" value={inboxData?.totalSession} />
                    <InfoRow label="Page View" value={inboxData?.allPageView} />
                    <InfoRow label="Time Spent" value={`2h`} />
                  </div>
                )}
              </div>
            </Card.Body>
          </Card>
        </div>
        {inboxData?.type === "inbox" ?
          <div className="w-100">
            <div className="preview-switcher gap-2">
              <button
                className={`flex-fill hstack gap-2 justify-content-center ${openHistory === "chat" ? "active" : ""
                  }`}
                onClick={() => setOpenHistory("chat")}
              >
                <BsChatLeft /> Chat
              </button>

              <button
                className={`flex-fill hstack gap-2 justify-content-center ${openHistory === "note" ? "active" : ""
                  }`}
                onClick={() => setOpenHistory("note")}
              >
                <BsChatLeft /> Note
              </button>

              <Dropdown className="flex-fill">
                <Dropdown.Toggle
                  as="button"
                  className={`hstack gap-2 justify-content-center ${MoreActionOption.some(item => item.label === openHistory) ? "active" : ""
                    }`}
                  style={{ width: "-webkit-fill-available" }}
                >
                  {MoreActionOption.find(item => item.label === openHistory)?.icon} {MoreActionOption.some(item => item.label === openHistory) ? openHistory : "More"}
                  <FiChevronDown />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {MoreActionOption.map((item, index) => (
                    <Dropdown.Item
                      key={index}
                      className="hstack gap-2"
                      onClick={() => {
                        setOpenHistory(item.label);
                      }}
                      active={openHistory === item.label}
                    >
                      {item.icon} {item.label}
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </div>
            <div className={`overflow-auto`} style={{ height: "calc(100vh - 152px)" }}>
              <div className="preview-scroll">
                {openHistory === "chat" &&
                  <InboxChats
                    inboxData={inboxData}
                    currentUserData={currentUserData}
                    getSingleInbox={getSingleInbox}
                    setMessages={setMessages}
                    messages={messages}
                    setSessionId={setSessionId}
                    sessionId={sessionId}
                    deleteSession={deleteSession}
                    setOpenHistory={setOpenHistory}
                    category={category}
                  />
                }
                {openHistory === "note" &&
                  <ContactNotes
                    contactId={inboxData?.contactId}
                  />
                }
                {openHistory === "Twilio SMS" && (
                  <ContactSMS />
                )}
                {openHistory === "Ticket" && (
                  <ContactTickets />
                )}
                {openHistory === "WhatsApp" && (
                  <ContactTickets />
                )}
              </div>
            </div>
          </div>
          :
          <div className="w-100">
            <div className={`overflow-auto`} style={{ height: "calc(100vh - 106px)" }}>
              <div className="preview-scroll">
                <InboxChats
                  inboxData={inboxData}
                  currentUserData={currentUserData}
                  getSingleInbox={getSingleInbox}
                  setMessages={setMessages}
                  messages={messages}
                  setSessionId={setSessionId}
                  sessionId={sessionId}
                  deleteSession={deleteSession}
                  setOpenHistory={setOpenHistory}
                  category={category}
                />
              </div>
            </div>
          </div>
        }
      </div>
    </>
  );
};

export default InboxDetails;
